<template>
  <div>
    <app-cells>
      <app-button @click.native="onShowModal">Import</app-button>
      <div class="date-picker-wrapper">
        <VueCtkDateTimePicker
          id="date_picker_balance"
          class="custom-date-time-picker custom-date-time-picker--range"
          v-model="filter_dates"
          :custom-shortcuts="custom_shortcuts"
          shortcut="last30Days"
          @is-hidden="onIsHiddenPicker"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Select dates"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          range
          noClearButton
        />
        <div v-if="date_picker_error" class="date-picker-wrapper__error">Choose an end date</div>
      </div>
      <app-select
        @input="onChangeFilter"
        v-model="filter_account"
        :options="account_options"
        :reduce="item => item.id"
        placeholder="Account"
        class="select--300"
      />
      <app-filters
        v-model="filter_type"
        :filters="filter_type_options"
        name="filterType"
        @change="onChangeFilter"
      />
    </app-cells>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__account><div class="table-default__left">Account</div></template>
        <template #account="{ row }">
          <router-link
            :to="{ name: 'finance-pos-transactions-imports-detail', params: { id: row.id } }"
            class="table-default__link-border"
          >
            {{ row.account.name }}
          </router-link>
        </template>
        <template #error="{ row }">{{ row.stats.error }}</template>
        <template #success="{ row }">{{ row.stats.success }}</template>
        <template #new="{ row }">{{ row.stats.new }}</template>
        <template #skipped="{ row }">{{ row.stats.skipped }}</template>
        <template #status="{ row }">
          <span class="status-tag" :style="{ backgroundColor: row.status.color }">{{ row.status.name }}</span>
        </template>
        <template #imported_by="{ row }">
          <div v-if="row.created_by" class="table-default__controls">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.created_by.crop.crop_url)"
                alt="Avatar"
                v-tooltip="{ content: row.created_by.full_name, placement: 'top-start', offset: 10 }"
              >
            </div>
            {{ dateTimeFormat(row.created_at) }}
          </div>
          <span v-else>-</span>
        </template>
        <template #attachment="{ row }">
          <div class="table-default__controls">
            <button
              @click="onDownloadFile(row.file, row)"
              type="button"
            >
              <img src="@/assets/img/download-table-blue-icon.svg" alt="">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
      name="import-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideModal" class="popup__close" type="button"></button>
        <div class="popup__title">Import file</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Account" required>
              <app-select
                v-model="form.account"
                :options="account_options"
                :reduce="item => item.id"
                :clearable="false"
                placeholder="Account"
                :error="$v.form.account.$error"
              />
              <template #error>
                <div v-if="$v.form.account.$dirty && !$v.form.account.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="File" required>
              <app-uploader
                v-model="form.file"
                :extensions="['text/csv', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/zip', 'application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/bmp']"
                formDataFormats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF,CSV"
              />
              <template #error>
                <div v-if="$v.form.file.$dirty && !$v.form.file.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">Import</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import { getFinanceAccounts, getFinancePosTransactionsImports, postFinancePosTransactionsImports } from '@/http'
import moment from 'moment'
import { required } from 'vuelidate/lib/validators'
import { format, parseISO } from 'date-fns'
import axios from 'axios'

export default {
  name: 'FinancePosTransactionsImports',
  components: { VueCtkDateTimePicker },
  data() {
    return {
      page_loaded: false,
      custom_shortcuts: [
        {
          key: 'next30Days',
          label: 'Next 30 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(30, 'days')
            }
          }
        },
        {
          key: 'next60Days',
          label: 'Next 60 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(60, 'days')
            }
          }
        },
        {
          key: 'next90Days',
          label: 'Next 90 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(90, 'days')
            }
          }
        },
        { key: 'last30Days', label: 'Last 30 days', value: 30 },
        { key: 'thisYear', label: 'This year', value: 'year' },
      ],
      filter_dates: null,
      date_picker_error: false,
      filter_account: null,
      account_options: [],
      filter_type: '0',
      filter_type_options: [
        { value: '0', label: 'Success' },
        { value: '1', label: 'Error' },
      ],
      table: {
        items: [],
        columns: [
          'account',
          'error',
          'success',
          'new',
          'skipped',
          'status',
          'imported_by',
          'attachment',
        ],
        options: {
          headings: {
            'account': 'Account',
            'error': 'Error',
            'success': 'Success',
            'new': 'New',
            'skipped': 'Skipped',
            'status': 'Status',
            'imported_by': 'Imported by',
            'attachment': 'File',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
      form: {},
    }
  },
  validations: {
    form: {
      account: { required },
      file: { required },
    },
  },
  created() {
    this.$nextTick(() => {
      this.fetchItems()
    })
    getFinanceAccounts().then(response => { this.account_options = response.data })
  },
  methods: {
    fetchItems() {
      this.table_preloader = true
      getFinancePosTransactionsImports({
        page: this.pagination.page,
        page_size: 20,
        created_at__gte: this.filter_dates.start,
        created_at__lte: this.filter_dates.end,
        account: this.filter_account,
        exclude_status: this.filter_type === '0' ? 'error' : '',
        status: this.filter_type === '1' ? 'error' : '',
      })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.table_preloader = false
          this.page_loaded = true
        })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      const data = { ...this.form, file: this.form.file.id }
      postFinancePosTransactionsImports(data)
        .then(() => {
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'File imported',
          })
          this.onHideModal()
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onShowModal() {
      this.form = {}
      this.$v.form.$reset()
      this.$modal.show('import-modal')
    },
    onHideModal() {
      this.$modal.hide('import-modal')
    },
    onIsHiddenPicker() {
      if (this.filter_dates?.start && this.filter_dates?.end) {
        this.date_picker_error = false
        this.fetchItems()
      } else this.date_picker_error = true
    },
    onChangeFilter() {
      this.pagination.page = 1
      this.fetchItems()
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    onDownloadFile(doc_obj, row) {
      axios.get(doc_obj.file, { responseType: 'blob' })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          const extension = doc_obj.file_name.split('.').pop()
          const part_of_name = `${row.account.name} - ${row.status.name} - ${row.created_by.full_name} - ${this.dateTimeFormat(row.created_at)}`

          link.href = url
          link.setAttribute('download', `${part_of_name}.${extension}`)
          document.body.appendChild(link)
          link.click()
          URL.revokeObjectURL(url)
        })
    },
  }
}
</script>
